/**
 * Contact-photo crop dialog (DNKADM-0048 U2) — pick → crop → upload.
 *
 * The full client-side flow behind the MemberDetail header avatar's "Change
 * photo" affordance (U1 left the `dialogOpen` hook). Three steps in one modal:
 *
 *  1. **File pick** (`<input type="file" accept="image/jpeg,image/png,image/webp,
 *     image/heic">`) with CLIENT validation BEFORE cropping — type ∈ {JPEG, PNG,
 *     WebP, HEIC} and size ≤ 5 MB, else an inline error and no cropper.
 *  2. **Square crop** — the picked image in `react-easy-crop` (the authorized
 *     first runtime dependency; the interactive crop can't reasonably be
 *     hand-rolled). Square aspect, zoom + drag, live preview. HEIC that the
 *     browser can't decode for preview falls back to a no-crop "upload as-is"
 *     path (dink-api center-crops a non-square anyway) — noted inline.
 *  3. **Confirm** — export the selected square region to a **square `Blob`**
 *     (`canvas.toBlob`) and `setMemberPhoto(contactId, blob)` (the U1 multipart
 *     PUT). In-flight state on the button; on `200` we hand the result up
 *     (`onUploaded`) so the header swaps the avatar to the cache-busted
 *     `photoUrl` + raises the success toast + bumps the detail `reloadToken`.
 *
 * Error mapping reuses U1's {@link toMemberPhotoErrorMessage}: `400/413/415`
 * surface INLINE (re-validate / re-pick), `403` is raised as an error toast and
 * the dialog closes (it's an access problem, not a fixable input). Cancel at any
 * step closes without uploading.
 *
 * Bearer-only `/admin/**` (via U1's `setMemberPhoto`); never CSRF, never cookies.
 * The copy is local/clean (no GoHighLevel/GHL strings); the only backend-sourced
 * text is the error message, already scrubbed through `serverMessage` in U1's
 * mapper.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import { buildMembersMaps } from '@/theme/maps/members';
import { useToast } from '@/components/useToast';
import {
  setMemberPhoto,
  toMemberPhotoErrorMessage,
  type MemberPhotoResult,
} from '@/api/memberPhoto';

interface MemberPhotoDialogProps {
  contactId: string;
  open: boolean;
  /** Cancel / close without uploading. */
  onClose: () => void;
  /**
   * A successful upload (`200`). The caller swaps the header avatar to the
   * returned cache-busted `photoUrl`, raises the success toast, bumps the detail
   * `reloadToken`, and closes the dialog.
   */
  onUploaded: (result: MemberPhotoResult) => void;
}

/** Client input caps (mirror the DNKAPI-0104 server caps so we fail fast). */
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
/** The file-pick `accept` filter (matches {@link ACCEPTED_TYPES}). */
const ACCEPT = ACCEPTED_TYPES.join(',');
/** Edge of the exported square blob — generous; dink-api does the final 256² resize. */
const EXPORT_SIZE = 512;

const TYPE_ERROR = 'Use a JPG, PNG, or WebP image.';
const SIZE_ERROR = 'Image must be under 5 MB.';

/** A picked, client-validated file plus an object URL for the cropper preview. */
interface Picked {
  file: File;
  /** `URL.createObjectURL(file)` — the cropper `image` src; revoked on replace/close. */
  url: string;
  /**
   * True when the browser couldn't decode the image for the interactive preview
   * (typically HEIC in non-Safari). We then upload the file as-is and let
   * dink-api center-crop — no client crop geometry.
   */
  previewFailed: boolean;
}

/** Reject anything outside the accepted types / size cap (the inline copy). */
function validate(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type)) return TYPE_ERROR;
  if (file.size > MAX_BYTES) return SIZE_ERROR;
  return null;
}

/**
 * Render the selected crop region of `imageSrc` onto a square canvas and export a
 * square `Blob`. `area` is `react-easy-crop`'s `croppedAreaPixels` (a square,
 * since the cropper aspect is 1). We draw it into a fixed `EXPORT_SIZE` square so
 * the upload is bounded; dink-api does the authoritative 256² resize. Prefers
 * WebP, falling back to JPEG where `toBlob` won't encode WebP.
 */
async function cropToSquareBlob(imageSrc: string, area: Area): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = EXPORT_SIZE;
  canvas.height = EXPORT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get a 2D drawing context.');
  // Draw the (square) crop region scaled into the square canvas.
  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    EXPORT_SIZE,
    EXPORT_SIZE,
  );
  return canvasToBlob(canvas);
}

/** Load an image element from a src (object URL), rejecting on a decode failure. */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject(new Error('Could not load the image.')));
    img.src = src;
  });
}

/** `canvas.toBlob` as a promise — WebP first, JPEG fallback. */
function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const onBlob = (blob: Blob | null) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not export the cropped image.'));
    };
    canvas.toBlob(
      (webp) => {
        if (webp) resolve(webp);
        else canvas.toBlob(onBlob, 'image/jpeg', 0.92);
      },
      'image/webp',
      0.92,
    );
  });
}

export function MemberPhotoDialog({
  contactId,
  open,
  onClose,
  onUploaded,
}: MemberPhotoDialogProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const ids = useId();
  const titleId = `${ids}-title`;
  const inputId = `${ids}-file`;
  const showToast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [picked, setPicked] = useState<Picked | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Revoke the active object URL when it is replaced or the dialog unmounts, so a
  // re-pick / close never leaks a blob URL.
  useEffect(() => {
    return () => {
      if (picked) URL.revokeObjectURL(picked.url);
    };
  }, [picked]);

  const onCropComplete = useCallback((_area: Area, croppedAreaPixels: Area) => {
    setAreaPixels(croppedAreaPixels);
  }, []);

  function reset() {
    setPicked((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPixels(null);
    setError(null);
  }

  function close() {
    if (uploading) return;
    reset();
    onClose();
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Allow re-picking the same file (the input is cleared so onChange re-fires).
    e.target.value = '';
    if (!file) return;

    const validationError = validate(file);
    if (validationError) {
      // Inline error BEFORE cropping — keep any prior pick replaced/cleared.
      reset();
      setError(validationError);
      return;
    }

    // Valid file: replace any prior pick (revoking its URL) and open the cropper.
    setPicked((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return { file, url: URL.createObjectURL(file), previewFailed: false };
    });
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPixels(null);
    setError(null);
  }

  // The cropper couldn't decode the image (typically HEIC outside Safari): fall
  // back to uploading the file as-is (dink-api center-crops a non-square).
  function handlePreviewFailed() {
    setPicked((prev) => (prev ? { ...prev, previewFailed: true } : prev));
  }

  async function handleConfirm() {
    if (!picked || uploading) return;
    setError(null);
    setUploading(true);
    try {
      let blob: Blob;
      let filename: string;
      if (picked.previewFailed) {
        // No interactive crop available — upload the original; dink-api center-crops.
        blob = picked.file;
        filename = picked.file.name || 'photo';
      } else {
        if (!areaPixels) {
          // The cropper hasn't reported a region yet (no media-load). Re-pick.
          setError('The image is still loading. Try again in a moment.');
          setUploading(false);
          return;
        }
        blob = await cropToSquareBlob(picked.url, areaPixels);
        filename = 'photo.webp';
      }
      const result = await setMemberPhoto(contactId, blob, filename);
      // Success: hand up (the caller swaps the avatar + toast + reloadToken + close).
      reset();
      onUploaded(result);
    } catch (err) {
      // 403 is an access problem (not fixable by re-picking) → toast + close;
      // 400/413/415 are input problems → inline so the operator can re-pick.
      const status = errorStatus(err);
      const message = toMemberPhotoErrorMessage(err);
      if (status === 403) {
        setUploading(false);
        reset();
        onClose();
        showToast(message, 'error');
        return;
      }
      setError(message);
      setUploading(false);
    }
  }

  const showCropper = picked !== null && !picked.previewFailed;

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle
        id={titleId}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Change photo</Typography>
          <Typography variant="body2">
            Pick an image, then drag and zoom to frame a square.
          </Typography>
        </Box>
        <IconAction size="small" onClick={close} disabled={uploading} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        {/* File pick — the visible control is a labelled button over a hidden input
            (so the accept filter + native picker still drive validation). */}
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          accept={ACCEPT}
          onChange={handleFile}
          disabled={uploading}
          style={{ display: 'none' }}
        />
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<Icon name="upload" />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          sx={{ mb: 2 }}
        >
          {picked ? 'Choose a different image' : 'Choose an image'}
        </Button>
        <Typography variant="body2" sx={{ mb: 2, color: t.text2 }}>
          JPG, PNG, or WebP, up to 5 MB.
        </Typography>

        {showCropper && picked && (
          <>
            {/* Interactive square crop (react-easy-crop). The container needs an
                explicit height — the cropper fills it absolutely. */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 260,
                bgcolor: members.photoDialogSurface,
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Cropper
                image={picked.url}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                onMediaLoaded={() => setError(null)}
                mediaProps={{ onError: handlePreviewFailed }}
              />
            </Box>
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 1.5, mt: 1.5 }}
            >
              <Icon name="zoom_out" sx={{ color: t.textMuted, fontSize: 20 }} />
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.01}
                onChange={(_e, v) => setZoom(Array.isArray(v) ? v[0] : v)}
                aria-label="Zoom"
                sx={{ color: t.brand }}
              />
              <Icon name="zoom_in" sx={{ color: t.textMuted, fontSize: 20 }} />
            </Stack>
          </>
        )}

        {picked?.previewFailed && (
          <Alert severity="info" icon={<Icon name="info" />} sx={{ mt: 1 }}>
            This image can’t be previewed for cropping in your browser. It will be uploaded
            and squared automatically.
          </Alert>
        )}

        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button
          color="inherit"
          onClick={close}
          disabled={uploading}
          sx={{ color: t.text2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon name="check" />}
          onClick={() => void handleConfirm()}
          disabled={!picked || uploading}
        >
          {uploading ? 'Uploading…' : 'Save photo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The numeric HTTP status of a thrown error, when it's our typed `ApiError`. */
function errorStatus(err: unknown): number | null {
  if (err && typeof err === 'object' && 'status' in err) {
    const s = (err as { status: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return null;
}
